import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, sendRequest } from "./restUtil";
import { logError } from "services/logging";
import { apiQuery } from "utils/queries";
import { LocalStorageKeys } from "utils/storage";
const PREVIOUS_SEARCH_NUMBER_OF_ITEMS = 5;

export type DictionarySavedWord = {
  id: string;
  word: string;
  pos: string | null;
  notes: string | null;
  definition: string | null;
  translation: string | null;
  created: string | null;
  updated: string | null;
  audio: string | null;
};

export type DictionaryMeaning = {
  definition: string;
  examples: string[];
  domain: string | null;
  pronunciation: {
    audio: string | null;
    phonetic: string | null;
  };
};

type DictionaryPos = Record<string, DictionaryMeaning[]>;

type DictionarySearchParams = {
  text: string;
};

export type DictionarySearchResponse = {
  word: string;
  provider: string;
  pos: DictionaryPos;
};

export function searchDictionary(params: DictionarySearchParams) {
  return api.get<DictionarySearchResponse, DictionarySearchParams>(
    "/dictionary/search",
    params
  );
}

type GetPersonalDictionaryParams = {
  page?: number;
  size?: number;
  order_by?: "created" | "updated" | null;
  sort?: "asc" | "desc";
  filter?: string | null;
};

export type GetPersonalDictionaryResponse = {
  list: DictionarySavedWord[];
  total_count: number;
};

export const personalDictionaryQuery = apiQuery<
  GetPersonalDictionaryResponse,
  GetPersonalDictionaryParams
>({
  key: "/dictionary/saved",
});

type AddWordToDictionaryPayload = {
  word: string;
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

export function addWordToDictionary(data: AddWordToDictionaryPayload) {
  return api.post<AddWordToDictionaryPayload, DictionarySavedWord>(
    "/dictionary/saved",
    data
  );
}

export function useAddSavedWord() {
  const invalidateSavedWords = useSavedWordsInvalidation();
  return useMutation({
    mutationFn: addWordToDictionary,
    onSuccess: invalidateSavedWords,
    onError: logError,
  });
}

type UpdateDictionaryPayload = {
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

type UpdateDictionaryArgs = { id: string } & UpdateDictionaryPayload;

export function updateDictionary({ id, ...payload }: UpdateDictionaryArgs) {
  return api.patch<UpdateDictionaryPayload, DictionarySavedWord>(
    `/dictionary/saved/${id}`,
    payload
  );
}

type DictionaryExamplesParams = {
  id: string;
  count?: 1 | 2 | 3 | 4 | 5;
};

export type DictionaryExamplesResponse =
  | {
      noContent: true;
      body: {
        list?: string[];
      };
    }
  | {
      noContent: false;
      body: {
        list: string[];
      };
    };

export async function getDictionaryExamples(
  data: DictionaryExamplesParams
): Promise<DictionaryExamplesResponse> {
  // The undecoded response, to tell 204 (examples still generating) from a 200 with data.
  const resp = await sendRequest("GET", "/dictionary/examples", { query: data });
  if (resp.status === 204) {
    return { noContent: true, body: {} };
  }
  return { noContent: false, body: (await resp.json()) as { list: string[] } };
}

export function deleteWordFromDictionary(id: string) {
  return api.delete(`/dictionary/saved/${id}`);
}

export function useDeleteSavedWord() {
  const invalidateSavedWords = useSavedWordsInvalidation();
  return useMutation({
    mutationFn: deleteWordFromDictionary,
    onSuccess: invalidateSavedWords,
    onError: logError,
  });
}

// Adding or deleting a word invalidates every cached page of the saved list. The
// invalidation is deliberately not returned, so a caller's own onSuccess runs
// without waiting for the refetch.
function useSavedWordsInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: personalDictionaryQuery.queryKeyPrefix,
    });
  };
}

export function addItemToPreviousSearch(item: string) {
  let previousSearch = getPreviousSearch();

  if (previousSearch.includes(item)) {
    return previousSearch;
  }

  previousSearch = [...previousSearch, item];
  if (previousSearch.length > PREVIOUS_SEARCH_NUMBER_OF_ITEMS) {
    previousSearch = previousSearch.slice(-PREVIOUS_SEARCH_NUMBER_OF_ITEMS);
  }

  localStorage.setItem(
    LocalStorageKeys.dictionaryPreviousSearch,
    JSON.stringify(previousSearch)
  );
  return previousSearch;
}

export function getPreviousSearch(): string[] {
  const previousSearch = localStorage.getItem(LocalStorageKeys.dictionaryPreviousSearch);
  if (!previousSearch) {
    return [];
  }

  const parsed = JSON.parse(previousSearch) as unknown;
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter((item): item is string => typeof item === "string");
}
